import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {ZoneLayer} from '../shared/cesium/layers/zone.layer';
import {PredictService} from './predict.service';
import {HighwayCIVLayer} from '../shared/cesium/layers/CIV/higway_civ.layer';
import {RoadCIVLayer} from '../shared/cesium/layers/CIV/road_civ.layer';
import {RailwayCIVLayer} from '../shared/cesium/layers/CIV/railway_civ.layer';
import {Layer, LayerLoadType, MapperViewer} from '../shared/cesium/types';
import {MapperService} from './mapper.service';
import {WatercourseCIVLayer} from '../shared/cesium/layers/CIV/watercourse_civ.layer';
import {PlacesCIVLayer} from '../shared/cesium/layers/CIV/places_civ.layer';
import {ZmiSEMLayer} from '../shared/cesium/layers/SEN/ZmiSEMLayer';
import {ZmiSEMP100Layer} from '../shared/cesium/layers/SEN/ZmiSEMP100Layer';
import {RoadLayer} from '../shared/cesium/layers/FRA/road.layer';
import {RailwayLayer} from '../shared/cesium/layers/FRA/railway.layer';
import {HydroSurfaceLayer} from '../shared/cesium/layers/FRA/hydroSurface.layer';
import {WatercourseLayer} from '../shared/cesium/layers/FRA/watercourse.layer';
import {PlacesLayer} from '../shared/cesium/layers/FRA/places.layer';
import {Building3dLayer} from '../shared/cesium/layers/FRA/building3d';
import {ZmiLayer} from '../shared/cesium/layers/zmi.layer';
import {ZmsLayer} from '../shared/cesium/layers/zms.layer';
import {ZiparuLayer} from '../shared/cesium/layers/CIV/ZiparuLayer';
import {PlanLayer} from '../shared/cesium/layers/default/picto.layer';
import {ZmfLayer} from '../shared/cesium/layers/FRA/Zmf.layer';
import {ZmffLayer} from '../shared/cesium/layers/FRA/zmff.layer';
import {ZmTsunamiLayer} from '../shared/cesium/layers/zm-tsunami.layer';
import {ZmTmdLayer} from '../shared/cesium/layers/zm-tmd.layer';

@Injectable({ providedIn: 'root' })
export class LayerService {
  private viewer: MapperViewer;
  private layers: Layer[] = [];
  private layersSubject = new BehaviorSubject<Layer[]>([]);
  layers$ = this.layersSubject.asObservable();

  constructor(
    public predictService: PredictService,
    public mapperService: MapperService
  ) {}

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;

    /* Generic layers */
    this.addLayer(new ZoneLayer(this.predictService, this.mapperService));
    this.addLayer(new PlanLayer(this.mapperService));

    /* Custom layers */
    switch (viewer.plan.type_zone) {
      case 'GLP':
      case 'GUF':
      case 'REU':
      case 'MTQ':
      case 'MYT':
      case 'BLM':
      case 'SPM':
      case 'MAF':
      case 'FRA':

        this.addLayer(new RoadLayer());
        this.addLayer(new RailwayLayer());
        this.addLayer(new HydroSurfaceLayer());
        this.addLayer(new PlacesLayer());
        this.addLayer(new WatercourseLayer());


        this.addZmLayers(viewer.plan.type_risque);

        if (this.viewer.canvasMode === '3d') {
          this.addLayer(new Building3dLayer());
        }

        break;
      case 'CIV':
        this.addLayer(new RoadCIVLayer());
        this.addLayer(new HighwayCIVLayer());
        this.addLayer(new RailwayCIVLayer());
        this.addLayer(new WatercourseCIVLayer());
        this.addLayer(new PlacesCIVLayer());
        this.addLayer(new ZiparuLayer());
        break;

      case 'SEN':
        this.addLayer(new ZmiSEMLayer());
        this.addLayer(new ZmiSEMP100Layer());
    }
  }

  private addZmLayers(typeRisque: string): void {
    switch (typeRisque) {
      case 'mvt':
        this.addLayer(new ZmTmdLayer(this.predictService));
        break;
      case 'feu':
        this.addLayer(new ZmfLayer());
        this.addLayer(new ZmffLayer(this.predictService));
        break;
      case 'tsunami':
        this.addLayer(new ZmTsunamiLayer(this.predictService));
        break;
      case 'tmd':
        this.addLayer(new ZmTmdLayer(this.predictService));
        break;
      case 'inondation':
        this.addLayer(new ZmiLayer(this.predictService));
        break;
      case 'submersion':
        this.addLayer(new ZmsLayer(this.predictService));
        break;
    }
  }

  addLayer(layer: Layer): void {
    layer.initialize(this.viewer);

    if (layer.loadType === LayerLoadType.Instant) {
      layer.loadData();
    }

    this.layers.push(layer);
    this.layersSubject.next(this.layers);
  }

  getLayer(id: string): Layer | undefined {
    return this.layers.find(l => l.id === id);
  }

  getActiveLayers(): Layer[] {
    return this.layers.filter(l => l.active);
  }

  requestRender(): void {
    this.viewer?.scene?.requestRender();
  }

  clearLayers(): void {
    this.layers.forEach((layer: Layer) => {
      layer.remove();
    });

    this.layers = [];

    this.layersSubject.next(this.layers);
  }
}
